import { useState, useCallback, useEffect, useMemo, useRef, type ComponentType } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowRightLeft,
  BarChart3,
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  User,
} from 'lucide-react'
import { useSurface } from '@/hooks/useGuideRegister'
import { useSessions, useSession } from '@/hooks/useSessions'
import { useDeferredFirstSend } from '@/hooks/useDeferredFirstSend'
import {
  navigateToSession,
  CHAT_AGENT_SLUG,
  ROSTER_AGENT_SLUG,
  HANDOVER_AGENT_SLUG,
  agentFullScreenUrl,
  sessionSurfaceSlug } from '@/lib/sessionRouting'
import { useHomeChildReturn } from '@/hooks/useHomeChildReturn'
import { useActiveShiftAssignments } from '@/hooks/useShifts'
import { usePatients, usePatientsConfig } from '@/hooks/usePatients'
import { useCurrentUser } from '@/hooks/useAuth'
import { useAuthStore } from '@/stores/auth'
import { useBackendHealth } from '@/hooks/useBackendHealth'
import { useAgentCatalogue, useAgentLabel, useLaunchableAgentCatalogue } from '@/hooks/useAgentCatalogue'
import { resolveAgentIdentity, type AgentIdentity } from '@/lib/agentIdentity'
import { AgentBadge } from '@/components/AgentBadge'
import { formatPatientLabel } from '@/lib/patientLabel'
import { isMeaningfulSourceLabel } from '@/lib/sourceLabel'
import { compareIsoDesc } from '@/utils/dates'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { EmptyState, ErrorWithRetry } from '@/components/clinical'
import {
  ConversationCard,
  RenameSessionDialog,
  ArchiveSessionDialog,
  DeleteSessionDialog,
} from '@/components/session'
import { HealthIndicator } from '@/components/HealthIndicator'
import { useApprovalsInbox } from '@/components/approvals/ApprovalsInbox'
import { APPROVAL_BADGE_MAX } from '@/lib/constants'
import ShareDialog from '@/components/ShareDialog'
import HandoverDialog from '@/components/handover/HandoverDialog'
import { HandoverCard } from '@/components/handover/HandoverCard'
import { MarkdownContent, MessageList, ChatInput } from '@/components/chat'
import {
  faceRequiresSession,
  getAgentDetailView,
  type AgentDetailViewProps,
} from '@/components/agentDetail/agentDetailViews'
import { AgentFrame } from '@/components/agentDetail/AgentFrame'
import {
  agentEntryPathForSession,
  opensOffAgentFace,
} from '@/components/agentDetail/agentSessionEntry'
import { MAIN_APP_ROUTES, agentFrameBasePath, agentPathFromFramePath, agentSlugFromFramePath } from '@/lib/routes'
import { shiftsToEntries } from '@/components/roster/rosterEntries'
import { RosterScheduleCard } from '@/components/roster/RosterScheduleCard'
import { useRosterShifts, useRosterTeamMembers, useRosterTeams } from '@/hooks/useRoster'
import { useUIPrefsStore } from '@/stores/uiPrefs'
import { useHomeNavStore, type HomeScope, type AgentFaceSelection } from '@/stores/homeNav'
import { useFabricRegistryStore } from '@/stores/fabricRegistry'
import {
  isPatientSelectionValid,
  resolveDeepLinkAdoption,
  resolveEffectiveAgentFace,
  resolveEffectivePatientId,
  resolveFacePatientId,
  resolveFallbackAgentFace,
  resolveHomeLevel,
} from '@/lib/homeSelection'

import type { AgentInfo } from '@/api/types'
import type { MessageResponse, SessionResponse } from '@/api/types_session'
import { CONVERSATION_MESSAGE_TYPE, conversationCitations, conversationText } from '@/api/types_session'
import type { AssignmentHandover } from '@/api/endpoints/shifts'
import { ConversationContext } from '@/components/chat/conversationContext'
import { AgentLauncherContext, type AgentLauncher } from '@/lib/agentLauncher'

/** Home scope tabs: patient work, the nurse's own (Persönlich), ward-level (Team).
 *  Sourced from the homeNav store so the union has one home (§DRY). */
type Scope = HomeScope

// Module-const empty arrays so derived selectors never produce a fresh
// reference each render (avoids needless re-memoisation churn).
const EMPTY_SESSIONS: SessionResponse[] = []

// Whether pane 3 can still show a FACE for this agent: exactly the condition
// BoardFace/ChatFace need a registry entry for. Module-level so it is a stable
// memo dependency. Not the launch policy (roster + compatibility have entries
// but are launched eagerly with a session) — the question here is narrower:
// would rendering this slug produce a pane with nothing in it?
const canRenderAgentFace = (slug: string): boolean => getAgentDetailView(slug) != null

interface PatientGroup {
  patientId: string
  patientSessions: SessionResponse[]
}

// content_types whose `content` is a structured envelope (raw JSON); the
// user-facing form lives in `blocks`, so don't render content as markdown.
// Mirrors MessageBubble.STRUCTURED_ENVELOPE_CONTENT_TYPES — keep in sync.
export default function Home() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useCurrentUser()
  const meId = user?.id
  const canViewAdmin = (user?.isPlatformAdmin ?? false) || (user?.adminView ?? false)
  const twinName = useAuthStore((state) => state.user?.twinName)
  const labelFormat = usePatientsConfig()?.label_format
  const { isHealthy, isStarting, isChecking } = useBackendHealth()
  const agentLabel = useAgentLabel()

  const { count: approvalsCount, isError: approvalsError } = useApprovalsInbox()

  const catalogue = useAgentCatalogue()
  // The launcher/manifest view of the catalogue: agents this caller may not
  // open are hidden (a nurse must never see a roster/team-access tile that
  // 403s on tap). The FULL catalogue above stays for identity resolution only
  // — an existing session keeps its accent/icon even when its agent is
  // restricted for this caller.
  const launchable = useLaunchableAgentCatalogue()
  // Resolve identity (accent/icon/scope) from the config catalogue. Pure +
  // memoised on the catalogue so the closure is stable across renders.
  const identityFor = useCallback(
    (slug: string) => resolveAgentIdentity(slug, catalogue),
    [catalogue],
  )

  // Fabric: this page is the "home" surface (design doc §3 concept 1).
  useSurface('home')

  const {
    data: sessions,
    isLoading: isSessionsLoading,
    isError: isSessionsError,
    isFetching: isSessionsFetching,
    refetch: refetchSessions,
  } = useSessions()

  const {
    openShift,
    assignments: activeAssignments,
    patientIds: activePatientIdList,
    isLoading: isShiftsLoading,
    isError: isShiftsError,
    isFetching: isShiftsFetching,
    refetch: refetchShifts,
  } = useActiveShiftAssignments({ enabled: !!meId })

  const { data: patients } = usePatients()


  // Workspace navigation state (scope + selection) lives in the homeNav store, not
  // local useState, so it survives leaving Home — opening an agent (inline route
  // or full-page sub-app hop) and the header links all unmount/reload this page.
  // Without this the rail reset to the first patient on every return. The whole
  // position persists across reloads; `selectionTouched` (this-load-only) does not.
  const scope = useHomeNavStore((s) => s.scope)
  const setScope = useHomeNavStore((s) => s.setScope)
  const selectedPatientId = useHomeNavStore((s) => s.selectedPatientId)
  const setSelectedPatientId = useHomeNavStore((s) => s.setSelectedPatientId)
  const selectedConversationId = useHomeNavStore((s) => s.selectedConversationId)
  const setSelectedConversationId = useHomeNavStore((s) => s.setSelectedConversationId)
  // The agent open in pane 3 with NO session yet — set on launch instead of
  // eagerly creating one; see launchAgent. Mutually exclusive with
  // selectedConversationId (each store setter clears the other).
  const selectedAgentFace = useHomeNavStore((s) => s.selectedAgentFace)
  const setSelectedAgentFace = useHomeNavStore((s) => s.setSelectedAgentFace)
  // Whether the selection above was chosen in THIS load or restored from a
  // previous one — only the narrow (phone) layout cares; see data-panes below.
  const selectionTouched = useHomeNavStore((s) => s.selectionTouched)
  const sidebarCollapsed = useUIPrefsStore((s) => s.sidebarCollapsed)
  const toggleSidebar = useUIPrefsStore((s) => s.toggleSidebar)
  const [previousOpen, setPreviousOpen] = useState(false)
  // The just-launched session, shown immediately while the list refetch catches
  // up (backend read-after-write lag). Only ever used as a fallback for its own id.
  const [pendingSession, setPendingSession] = useState<SessionResponse | null>(null)

  // Child sub-app return landing on the workspace: a run launched FROM here
  // returns with `?from_child=<child>&parent_session=<parent>` (see
  // useSessionHandoff). The hook strips the params, steers scope/patient/
  // conversation to the parent, and records the child's summary — extracted
  // (and unit-tested) in useHomeChildReturn.
  useHomeChildReturn()

  // Dialog state
  const [handoverOpen, setHandoverOpen] = useState(false)
  const [renameDialog, setRenameDialog] = useState<SessionResponse | null>(null)
  const [archiveDialog, setArchiveDialog] = useState<SessionResponse | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<SessionResponse | null>(null)
  const [shareDialog, setShareDialog] = useState<{ id: string; title: string } | null>(null)

  // ----- patient labels ---------------------------------------------------
  const labelByPatientId = useMemo(() => {
    const map = new Map<string, string>()
    for (const p of patients ?? []) {
      map.set(p.token, formatPatientLabel(p.attributes, labelFormat, p.token))
    }
    return map
  }, [patients, labelFormat])

  const patientLabel = useCallback(
    (patientId: string) =>
      labelByPatientId.get(patientId) ?? formatPatientLabel({}, labelFormat, patientId),
    [labelByPatientId, labelFormat],
  )

  const activePatientIds = useMemo(() => new Set(activePatientIdList), [activePatientIdList])

  // ----- bucketing --------------------------------------------------------
  // Sessions with a patient frame, grouped by patient_id.
  const sessionsByPatient = useMemo(() => {
    const map = new Map<string, SessionResponse[]>()
    for (const s of sessions ?? []) {
      if (s.subject?.type !== 'patient') continue
      const list = map.get(s.subject.id)
      if (list) list.push(s)
      else map.set(s.subject.id, [s])
    }
    return map
  }, [sessions])

  // Current-shift patients, ordered by assignment start, each with its sessions.
  const currentPatients = useMemo<PatientGroup[]>(() => {
    const seen = new Set<string>()
    const ordered: PatientGroup[] = []
    const sorted = [...activeAssignments].sort((a, b) => compareIsoDesc(a.started_at, b.started_at))
    for (const a of sorted) {
      if (seen.has(a.patient_id)) continue
      seen.add(a.patient_id)
      ordered.push({
        patientId: a.patient_id,
        patientSessions: sessionsByPatient.get(a.patient_id) ?? EMPTY_SESSIONS,
      })
    }
    return ordered
  }, [activeAssignments, sessionsByPatient])

  // Non-patient sessions, split by the AGENT's config scope: team-scoped agents
  // (patient allocation, shift planning) → Team; everything else is the nurse's
  // own (knowledge profile, general chat) → Persönlich. Flat, newest-first (API
  // order preserved) — no agent grouping; each card carries its agent badge.
  const { personalSessions, teamSessions } = useMemo(() => {
    const personal: SessionResponse[] = []
    const team: SessionResponse[] = []
    for (const s of sessions ?? []) {
      // A session about a patient is the patient's (above); one about anything
      // else (a protocol draft or version the Studio works on) is that
      // subject's work, not one of the nurse's conversations.
      if (s.subject) continue
      if (identityFor(sessionSurfaceSlug(s)).scope === 'team') team.push(s)
      else personal.push(s)
    }
    return { personalSessions: personal, teamSessions: team }
  }, [sessions, identityFor])

  const previousPatientIds = useMemo(() => {
    const ids: string[] = []
    for (const patientId of sessionsByPatient.keys()) {
      if (!activePatientIds.has(patientId)) ids.push(patientId)
    }
    return ids
  }, [sessionsByPatient, activePatientIds])

  // ----- desktop derived selection (no effects — derive defaults) ---------
  // The stored selection is validated against the live shift (see homeSelection):
  // a persisted/stale selectedPatientId no longer on shift is ignored so it can't
  // wedge the rail on an unlisted patient; when valid we honour it, else fall back
  // to the first current patient.
  const currentPatientIds = useMemo(
    () => currentPatients.map((p) => p.patientId),
    [currentPatients],
  )
  const hasValidPatientSelection = isPatientSelectionValid(selectedPatientId, currentPatientIds)
  const effectivePatientId = resolveEffectivePatientId(selectedPatientId, currentPatientIds)
  const selectedPatientSessions = effectivePatientId
    ? sessionsByPatient.get(effectivePatientId) ?? EMPTY_SESSIONS
    : EMPTY_SESSIONS

  // The nurse's CURRENT assignment of the selected patient — the row a handover
  // is keyed on (outgoing), and the row whose handover payload (incoming) is
  // shown while this custody lasts.
  const selectedPatientAssignment = useMemo(
    () =>
      effectivePatientId
        ? activeAssignments.find(
            (a) => a.patient_id === effectivePatientId && !a.ended_at,
          ) ?? null
        : null,
    [activeAssignments, effectivePatientId],
  )

  // The flat, ordered session list for the active scope — drives the default
  // pane-3 selection.
  const scopeFlatSessions = useMemo<SessionResponse[]>(() => {
    if (scope === 'patient') return selectedPatientSessions
    return scope === 'team' ? teamSessions : personalSessions
  }, [scope, selectedPatientSessions, personalSessions, teamSessions])

  // Validate the explicit selection against the active scope's list so a stale
  // cross-scope / cross-patient selection auto-resets to the scope's latest
  // (keeps pane 3 in sync with pane 1 without an effect).
  const selectionInScope =
    selectedConversationId != null &&
    (scopeFlatSessions.some((s) => s.id === selectedConversationId) ||
      pendingSession?.id === selectedConversationId)
  // The no-explicit-selection fallback is "the scope's latest CONVERSATION" — a
  // quiz run is skipped. A run's transcript is training_step messages that fold
  // to nothing inline, so auto-opening the newest session right after a nurse
  // starts a quiz would hand pane 3 an empty chat strip.
  // Clicking a run card still resumes it, in the runner (renderCard).
  // ...but SKIPPING it must not walk past the run to a DIFFERENT agent — see
  // resolveFallbackAgentFace, which owns that derivation (pure + unit-tested,
  // alongside the patient-rail default).
  const newestInScope = scopeFlatSessions[0]
  const fallbackAgentFace = useMemo<AgentFaceSelection | null>(
    () => resolveFallbackAgentFace(newestInScope, selectionInScope, opensOffAgentFace),
    [selectionInScope, newestInScope],
  )
  // A launched face always outranks the derived one — but only while pane 3 can
  // still render that agent. The face is persisted now, so its slug can be one
  // the registry no longer has (rename, removal) after a deploy; resolving it
  // here keeps that from rendering a dead pane 3. See resolveEffectiveAgentFace.
  const effectiveAgentFace = useMemo(
    () => resolveEffectiveAgentFace(selectedAgentFace, fallbackAgentFace, canRenderAgentFace),
    [selectedAgentFace, fallbackAgentFace],
  )
  // The patient frame pane 3 works in — the same one pane 1 and pane 2 show, so
  // a face and the rail can never point at different patients.
  const facePatientId = resolveFacePatientId(scope, effectivePatientId)
  const effectiveConversationId =
    (selectionInScope
      ? selectedConversationId
      : effectiveAgentFace
        ? null
        : scopeFlatSessions.find((s) => !opensOffAgentFace(s))?.id) ?? null
  // A freshly-launched session lives in `pendingSession` until the list refetch
  // includes it; the real list row takes precedence as soon as it lands.
  const selectedConversation = useMemo(
    () =>
      (sessions ?? []).find((s) => s.id === effectiveConversationId) ??
      (pendingSession?.id === effectiveConversationId ? pendingSession : null),
    [sessions, effectiveConversationId, pendingSession],
  )

  // The pane's inner screen lives in the URL, because the agent's own routes do
  // (AgentFrame). Selection stays the pane's identity — this is the one place
  // that opens the screen that selection MEANS: a quiz run resumes in the
  // runner, everything else on the agent's face. Keyed on the selection, not
  // run on every render, so a screen's own push (the board starting a quiz)
  // is not immediately rewritten back to the entry screen.
  // A deep link INTO an agent's frame (/home/a/<slug>/…) is itself an
  // instruction about pane 3 — from a reload, the Back button, or a link a
  // colleague sent. It has to beat the selection this browser last persisted,
  // or the screen the URL names is replaced by the one the store remembers.
  // Read LIVE, not once at mount: `/home/*` is a single route (App.tsx), so a
  // client-side push into another agent's frame — the twin handing a protocol
  // to the runner — never remounts Home. A snapshot taken on the first render
  // can only ever describe the URL the page loaded with, which is why that
  // handoff moved the address bar and nothing else until the nurse refreshed.
  const { pathname } = useLocation()
  const deepLinkedSlug = agentSlugFromFramePath(pathname)

  // The pane's inner screen lives in the URL, because the agent's own routes do
  // (AgentFrame). Selection stays the pane's identity — this is the one place
  // that opens the screen that selection MEANS: a run resumes on the agent's
  // own screen, everything else on its face. Keyed on the selection, not run on
  // every render, so a screen's own push (the board starting a quiz) is not
  // rewritten back to the entry screen.
  const openedSelectionRef = useRef<string | null>(
    deepLinkedSlug ? `face:${deepLinkedSlug}` : null,
  )
  // Each deep-linked slug gets exactly ONE consideration, tracked by the
  // slug itself rather than a boolean — see resolveDeepLinkAdoption
  // (lib/homeSelection.ts) for the full rationale (re-adoption after a later
  // clear, and a second different deep link in the same un-remounted page
  // life both stay handled). This ref is the one piece of that decision Home
  // owns: WHERE the slug a prior pass resolved is kept, until the URL leaves
  // the frame.
  const deepLinkConsideredRef = useRef<string | null>(null)
  useEffect(() => {
    // Leaving the frame ends the consideration. The next push into the SAME
    // agent (the twin's second protocol handoff in one page life) is a new
    // instruction; keeping the slug refused it, so the address bar moved and
    // pane 3 stayed on the twin until a reload remounted Home.
    if (deepLinkedSlug == null) deepLinkConsideredRef.current = null
    // Adopting the deep link is a state update, so the derived selection still
    // reads as the old one for the rest of this commit. Take the pass; the next
    // one runs against the adopted face and agrees with the address bar.
    const deepLink = resolveDeepLinkAdoption(
      deepLinkedSlug,
      deepLinkConsideredRef.current,
      effectiveAgentFace?.slug ?? null,
      canRenderAgentFace,
    )
    if (deepLink.adopt) {
      // The URL IS the opened screen. Without this the next pass recomputes
      // `key` as the newly adopted face, misses the ref, and navigates to the
      // agent's entry screen — replacing the deep link it just adopted. On a
      // cold load the mount-time seeding above says this already; a
      // client-side deep link has to say it at the moment it is adopted.
      openedSelectionRef.current = `face:${deepLinkedSlug}`
      setSelectedAgentFace({ slug: deepLinkedSlug as string })
      return
    }
    // Recorded only once the adoption has been OBSERVED — on a pass that did
    // not itself adopt. Marking it on the adopting pass records a decision
    // whose effect has not landed yet: `effectiveAgentFace` still reads null
    // for the rest of that commit, so a repeat of this pass before the state
    // arrives (StrictMode double-invokes every mount effect, and any dep can
    // change in between) is refused re-adoption AND falls through below with
    // no face and no conversation — computing `key = null`, clearing
    // openedSelectionRef, and rewriting the deep link away to /home. Adopting
    // is idempotent, so repeating it is harmless; recording it early is not.
    if (deepLink.consider) {
      deepLinkConsideredRef.current = deepLinkedSlug
    }
    const key = effectiveAgentFace ? `face:${effectiveAgentFace.slug}` : effectiveConversationId
    if (key === openedSelectionRef.current) return
    // A selected conversation the list hasn't produced yet: wait for it rather
    // than opening the face and pulling the nurse off the run she picked.
    if (!effectiveAgentFace && effectiveConversationId && !selectedConversation) return
    openedSelectionRef.current = key
    const entry = selectedConversation ? agentEntryPathForSession(selectedConversation) : '/'
    const target =
      selectedConversation && entry !== '/'
        ? `${agentFrameBasePath(sessionSurfaceSlug(selectedConversation))}${entry}`
        : MAIN_APP_ROUTES.home
    if (window.location.pathname !== target) navigate(target, { replace: true })
  }, [
    deepLinkedSlug,
    effectiveAgentFace,
    effectiveConversationId,
    selectedConversation,
    setSelectedAgentFace,
    navigate,
  ])

  // Tapping a session card means OPEN THIS SESSION, which is not the same as
  // "select it": the pane's inner screen lives in the URL now, and an agent's
  // own screen can move it (a finished run navigates back to the board) while
  // the selection stays put. Selecting alone would then be a dead tap on the
  // card of the session already selected — the way back into a finished run.
  const openSessionInPane = useCallback(
    (session: SessionResponse) => {
      setSelectedConversationId(session.id)
      const entry = agentEntryPathForSession(session)
      const target =
        entry === '/' ? MAIN_APP_ROUTES.home : `${agentFrameBasePath(sessionSurfaceSlug(session))}${entry}`
      openedSelectionRef.current = session.id
      if (window.location.pathname !== target) navigate(target)
    },
    [navigate, setSelectedConversationId],
  )

  // ----- handlers ---------------------------------------------------------
  // Launch a specific agent already framed to a patient: open its FACE in the
  // workspace with NO session — every agent now has an inline detail view
  // (chat, compat, care-planner), so the detail pane renders the agent in the frame.
  // The session itself is minted lazily, on the nurse's first composer send
  // (see AgentFacePane → ChatFace / BoardFace, both driven by the shared
  // useDeferredFirstSend hook) — tapping a tile alone must never POST
  // /sessions (a tap that goes nowhere used to mint a hidden 0-message
  // "orphan" session every time). Guards re-entry: a
  // rapid double-tap (or a tap while a launch is in flight) must not mint a
  // SECOND patient-scoped session on the sub-app-routing path below — each
  // call generates a fresh idempotency key, so without this guard concurrent
  // taps bypass server-side dedup. A sync ref (not state) catches same-tick
  // taps before React re-renders.
  const launchingRef = useRef(false)

  // Launch an agent in the workspace. A patientId frames the session to a
  // patient (Patienten scope); null launches without patient context under the
  // agent's own scope tab (Persönlich or Team, from its config scope).
  // Agents with an inline app (compat matrix, Care Planner board, protocol library, …)
  // open IN the frame: select the FACE so the detail pane renders the agent
  // working in the frame — never a full-screen bounce. Agents with no inline
  // view fall back to their own page (a sub-app entry URL needs a real session
  // id), so those still create eagerly.
  const launchAgent = useCallback(
    async (agent: AgentInfo, patientId: string | null) => {
      if (launchingRef.current) return
      // Handover is a COMPOSER, not a chat you open. Tapping the Handover tile
      // for a patient in the caller's own shift opens the handover composer
      // (incoming-nurse picker + curation) — the same flow as the "Hand over"
      // button — instead of minting a blank handover session with no colleague
      // (the dead-end empty chat in the old behaviour).
      // Without an owned active assignment there is nothing to hand over, so
      // it's a no-op rather than a blank session.
      if (agent.slug === HANDOVER_AGENT_SLUG) {
        if (
          patientId &&
          activeAssignments.some((a) => a.patient_id === patientId && !a.ended_at)
        ) {
          setScope('patient')
          setSelectedPatientId(patientId)
          setHandoverOpen(true)
        }
        return
      }
      // Protocols used to be excluded here and sent to alg.<tenant> with a hard
      // cross-origin navigation, which threw the app shell away. Its app is
      // its own service now (tricorder-studio-web), and getAgentDetailView
      // answers with that app in a frame (RemoteAgentFace), so protocols takes
      // the SAME render-in-frame path as every other agent with a face.
      if (getAgentDetailView(agent.slug)) {
        // Every launch is the deferred face flow: the face's composer creates
        // the session WITH its first message (creating a session runs it —
        // there is no idle create upstream). A board face renders without a
        // session: roster's board reads need none, and compatibility's matrix
        // is a build-time snapshot.
        setSelectedAgentFace({ slug: agent.slug })
        if (patientId) {
          setScope('patient')
          setSelectedPatientId(patientId)
        } else {
          setScope(identityFor(agent.slug).scope === 'team' ? 'team' : 'personal')
        }
        return
      }
      // No in-frame view: a sub-app agent opens on its own host, where its
      // face owns the same deferred create. navigateToSession needs a session
      // and there is none yet.
      const fullScreenUrl = agentFullScreenUrl(agent.slug, '/')
      if (fullScreenUrl) {
        window.location.href = fullScreenUrl
        return
      }
      setSelectedAgentFace({ slug: agent.slug })
      if (patientId) {
        setScope('patient')
        setSelectedPatientId(patientId)
      } else {
        setScope(identityFor(agent.slug).scope === 'team' ? 'team' : 'personal')
      }
    },
    [
      identityFor,
      activeAssignments,
      setSelectedAgentFace,
      setScope,
      setSelectedPatientId,
    ],
  )

  // The launch a surface in pane 3 asks for — a failed turn's "new
  // conversation" — is a tile's launch, in the open conversation's own patient
  // frame, so the new session is about the same patient as the one that failed.
  const agentLauncher = useMemo<AgentLauncher>(
    () => ({
      canLaunch: (slug) => launchable.some((a) => a.slug === slug),
      launch: (slug) => {
        const agent = launchable.find((a) => a.slug === slug)
        if (!agent) return
        const subject = selectedConversation?.subject
        void launchAgent(agent, subject?.type === 'patient' ? subject.id : null)
      },
    }),
    [launchable, launchAgent, selectedConversation],
  )

  // The FACE's composer (ChatFace, or a board wrapper's own side-channel) owns
  // its OWN useDeferredFirstSend call — it creates the session on first send;
  // this is just the transition it fires (onCreated) once that succeeds:
  // swap pane 3 from the face to the real conversation. pendingSession is the
  // same read-after-write bridge the old eager-create path already used.
  const handleFaceSessionCreated = useCallback(
    (session: SessionResponse) => {
      setPendingSession(session)
      setSelectedAgentFace(null)
      setSelectedConversationId(session.id)
      // Re-assert the session's own frame, exactly as the eager-create path
      // always has: if the nurse switched patient (or scope) while the create
      // was in flight, pane 3 would otherwise show patient A's new
      // conversation under patient B's rails — a cross-patient mismatch. The
      // created session is the truth; snap the selection back to it.
      if (session.subject?.type === 'patient') {
        setScope('patient')
        setSelectedPatientId(session.subject.id)
      }
    },
    [setSelectedAgentFace, setSelectedConversationId, setScope, setSelectedPatientId],
  )

  // Fabric navigate targets: EVERY config-visible agent is addressable as
  // home.agent.<slug>. The callback is launchAgent(), which renders an
  // in-frame agent in the detail pane AND routes a sub-app agent (e.g.
  // protocols → alg.<tenant>) out via navigateToSession — so registration
  // must NOT gate on getAgentDetailView, or every sub-app agent (protocols)
  // silently drops out of the twin's navigable targets. Registration derives
  // from the SAME per-user catalogue that renders the launcher tiles, so the
  // manifest never names an agent this user cannot see (ACL by construction).
  // labelKey carries the agent's display name, ALREADY LOCALISED — a non-key
  // string passes through i18n.t() verbatim, which is how the resolved label
  // survives fabricRegistry.uiContext()'s own t() call. It used to carry the
  // raw config name, so the manifest told the agent "Y-Site Compatibility
  // Specialist" and "Handover" while the tile beside it said "Kompatibilität"
  // and "Rapport". The agent then offered and coach-marked features by names
  // that appear nowhere in the nurse's interface. The manifest's whole claim
  // is that it describes what she is looking at.
  useEffect(() => {
    const reg = useFabricRegistryStore.getState()
    const unregisters = launchable
      .map((a) =>
        reg.register(
          {
            guideId: `home.agent.${a.slug}`,
            surfaceId: 'home',
            kind: 'navigate',
            labelKey: agentLabel(a.slug, a.name),
          },
          () => {
            // The SAME patient frame pane 2's tile tap passes (see its
            // onLaunchAgent). This was a literal `null`, and launchAgent
            // branches on the argument three times, so the agent's config
            // scope silently decided what the nurse got: personal and team
            // agents opened, `handover` hit `if (patientId && …) { … } return`
            // and could never do anything at all, `report` created a session
            // with no patient, and the remaining patient-scope agents opened
            // with scope forced to 'personal' and so resolved a null patient.
            // fabricGuide posts surfaceChanged after activate() regardless, so
            // the twin was told the navigate succeeded either way.
            void launchAgent(a, facePatientId)
          },
        ),
      )
    return () => {
      for (const u of unregisters) u()
    }
    // facePatientId is a dep: without it the registered callback keeps the
    // patient that was selected when this effect last ran. agentLabel is a dep
    // for the same reason in the other direction: it is memoised on i18n's `t`,
    // so switching language re-registers the manifest in the new one.
  }, [launchable, launchAgent, facePatientId, agentLabel])

  const handleSelectPatient = useCallback((patientId: string) => {
    setSelectedPatientId(patientId)
    setSelectedConversationId(null) // reset the open conversation to the new patient's latest
  }, [setSelectedPatientId, setSelectedConversationId])

  // Per-patient card meta for the rail: total conversations + how many are
  // flagged needs_review. Honest counts derived from the patient's sessions —
  // no invented acuity/vitals (handoff §"do not invent vitals").
  const patientMeta = useCallback(
    (id: string) => {
      const ss = sessionsByPatient.get(id) ?? EMPTY_SESSIONS
      return {
        total: ss.length,
        needsReview: 0,
      }
    },
    [sessionsByPatient],
  )

  const handleShareSession = useCallback(
    (session: SessionResponse) =>
      setShareDialog({ id: session.id, title: session.title || t('home.newConversation') }),
    [t],
  )

  // A card bound to its actions. `mode` decides what a tap does: navigate to the
  // conversation (mobile / single-column) or select it for the desktop pane 3.
  const renderCard = useCallback(
    (session: SessionResponse, mode: 'navigate' | 'select') => (
      <ConversationCard
        key={session.id}
        session={session}
        selected={
          // No card is "the open one" while an agent FACE holds pane 3 — the
          // latest-session fallback behind effectiveConversationId would
          // otherwise highlight a conversation that is not being shown.
          mode === 'select' && !effectiveAgentFace && session.id === effectiveConversationId
        }
        onClick={() =>
          // Select into the detail pane only when the agent has an in-frame
          // view. A sub-app agent without one (protocols → alg.<tenant>) must
          // route out via navigateToSession — selecting it inline would render
          // the global library picker instead of resuming THIS protocol run
          // (the alg.<tenant> regression).
          //
          // A learn RUN used to be excluded here too, because the pane could
          // only ever show an agent's face and a run does not belong on the
          // board — so resuming one meant leaving for the sub-app. The pane
          // now mounts the agent's declared route tree (AgentFrame), and the
          // agent itself says which screen a run opens at
          // (agentFrameEntryPath), so the exclusion is gone with the reason
          // for it.
          mode === 'select' && getAgentDetailView(sessionSurfaceSlug(session))
            ? openSessionInPane(session)
            : navigateToSession(session, (p) => navigate(p))
        }
        onRename={() => setRenameDialog(session)}
        onArchive={() => setArchiveDialog(session)}
        onDelete={() => setDeleteDialog(session)}
        onShare={() => handleShareSession(session)}
      />
    ),
    [
      navigate,
      openSessionInPane,
      effectiveConversationId,
      effectiveAgentFace,
      handleShareSession,
    ],
  )

  // Patient-scope agents power the per-patient launcher tile row.
  const patientAgents = useMemo(
    () => launchable.filter((a) => identityFor(a.slug).scope === 'patient' || a.slug === CHAT_AGENT_SLUG),
    [launchable, identityFor],
  )

  // Personal-scope agents power the Persönlich launcher — the nurse's own,
  // patient-independent agents (knowledge profile, general chat).
  const personalAgents = useMemo(
    () => launchable.filter((a) => identityFor(a.slug).scope === 'personal'),
    [launchable, identityFor],
  )

  // Team-scope agents power the Team launcher — ward-level work (patient
  // allocation, shift planning).
  const teamAgents = useMemo(
    () => launchable.filter((a) => identityFor(a.slug).scope === 'team'),
    [launchable, identityFor],
  )

  const scopeOptions: Array<{ id: Scope; label: string }> = [
    { id: 'patient', label: t('home.scope.patients') },
    { id: 'personal', label: t('home.scope.personal') },
    { id: 'team', label: t('home.scope.team') },
  ]

  return (
    <AgentLauncherContext.Provider value={agentLauncher}>
    <div className="home-shell bg-neutral-50">
      {/* Header — labeled actions at lg+ (the icon-only row is a named friction point) */}
      <header className="sticky top-0 z-10 shrink-0 bg-neutral-50/80 backdrop-blur-xl border-b border-neutral-200/50">
        <div className="px-4 h-14 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              type="button"
              onClick={toggleSidebar}
              className="home-sidebar-toggle shrink-0 -ml-1 w-9 h-9 rounded-lg flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 active:scale-95 transition"
              aria-pressed={sidebarCollapsed}
              aria-label={sidebarCollapsed ? t('home.showSidebar') : t('home.hideSidebar')}
              title={sidebarCollapsed ? t('home.showSidebar') : t('home.hideSidebar')}
            >
              {sidebarCollapsed ? (
                <PanelLeftOpen className="w-5 h-5" />
              ) : (
                <PanelLeftClose className="w-5 h-5" />
              )}
            </button>
            <h1 className="min-w-0 text-lg font-display font-semibold text-neutral-900 truncate">
              {twinName ? `${t('home.assistantPrefix')} ${twinName}` : t('home.title')}
            </h1>
            <HealthIndicator isHealthy={isHealthy} isStarting={isStarting} isChecking={isChecking} compact />
          </div>
          <nav className="flex items-center gap-1 shrink-0">
            <HeaderAction to="/approvals" label={t('home.headerAction.notices')} icon={<Bell className="w-5 h-5" />}>
              {approvalsCount > 0 ? (
                <span className="absolute top-0.5 right-0 min-w-[16px] h-4 px-1 rounded-full bg-primary text-white text-[10px] font-semibold flex items-center justify-center">
                  {approvalsCount > APPROVAL_BADGE_MAX ? `${APPROVAL_BADGE_MAX}+` : approvalsCount}
                </span>
              ) : approvalsError ? (
                <span
                  className="absolute top-1 right-1 w-2 h-2 rounded-full bg-tone-warn"
                  role="status"
                  aria-label={t('documents.approvals.loadError', 'Could not load pending approvals.')}
                />
              ) : null}
            </HeaderAction>
            <HeaderAction
              to={MAIN_APP_ROUTES.documents}
              label={t('home.headerAction.knowledge')}
              icon={<FileText className="w-5 h-5" />}
            />
            {canViewAdmin && (
              <HeaderAction
                to={MAIN_APP_ROUTES.adminAnalytics}
                label={t('home.admin', 'Admin')}
                icon={<BarChart3 className="w-5 h-5" />}
              />
            )}
            <Link
              to="/profile"
              className="ml-1 w-9 h-9 rounded-full bg-primary-50 flex items-center justify-center text-primary-700 font-semibold text-sm active:scale-95 transition-transform"
            >
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </Link>
          </nav>
        </div>
      </header>

      {/* Workspace — THREE panes for the patient scope (patient list ·
          conversations · open conversation), TWO for Persönlich / Team
          (flat conversation list · open). Side-by-side when the container is wide
          (data-panes picks the column count); a drill-down when narrow
          (data-level shows one pane at a time) — all in CSS (index.css
          .home-workspace), no viewport breakpoint. */}
      <div
        className="home-workspace"
        data-panes={scope === 'patient' ? '3' : '2'}
        data-sidebar={sidebarCollapsed ? 'collapsed' : 'expanded'}
        data-level={resolveHomeLevel({
          // Deliberately the RAW selections, not the effective ones, and gated
          // on selectionTouched — the rule and its rationale live with the
          // helper (pure + unit-tested, alongside the pane-3 derivations).
          selectionTouched,
          selectedConversationId,
          selectedAgentFace,
          scope,
          hasValidPatientSelection,
        })}
      >
        {/* Pane 1 — scope rail: the segmented control + the patient list
            (patient scope) or the scope's flat conversation list (else). */}
        <section className="home-pane home-pane-rail bg-white">
          <div className="shrink-0 px-3 py-3 border-b border-neutral-200">
            <SegmentedControl
              value={scope}
              onChange={(v) => {
                setScope(v)
                setSelectedPatientId(null)
                setSelectedConversationId(null)
              }}
              options={scopeOptions}
              className="w-full"
            />
          </div>
          {scope === 'patient' ? (
            <PatientRail
              currentPatients={currentPatients}
              activePatientId={effectivePatientId}
              patientLabel={patientLabel}
              patientMeta={patientMeta}
              onSelect={handleSelectPatient}
              onOpenPrevious={() => setPreviousOpen(true)}
              previousPatientCount={previousPatientIds.length}
              isLoading={isShiftsLoading}
              isError={isShiftsError}
              isFetching={isShiftsFetching}
              onRetry={() => void refetchShifts()}
            />
          ) : (
            <>
              <div className="flex-1 min-h-0 overflow-y-auto p-3">
                <SessionList
                  sessions={scope === 'team' ? teamSessions : personalSessions}
                  emptyTitle={t(scope === 'team' ? 'home.team.empty.title' : 'home.personal.empty.title')}
                  emptyDescription={t(
                    scope === 'team' ? 'home.team.empty.description' : 'home.personal.empty.description',
                  )}
                  renderCard={(s) => renderCard(s, 'select')}
                  isLoading={isSessionsLoading}
                  isError={isSessionsError}
                  isFetching={isSessionsFetching}
                  onRetry={() => void refetchSessions()}
                />
              </div>
              {/* Scope launcher — start one of the scope's agents (Persönlich:
                  knowledge profile, general chat; Team: allocation, shift
                  planning), mirroring the patient launcher. No patient
                  context → launchAgent(agent, null). */}
              <AgentLauncher
                agents={scope === 'team' ? teamAgents : personalAgents}
                onLaunch={(a) => void launchAgent(a, null)}
                label={t(scope === 'team' ? 'home.launcherTeam' : 'home.launcherPersonal')}
                agentLabel={agentLabel}
                identityFor={identityFor}
              />
            </>
          )}
        </section>

        {/* Pane 2 — the selected patient's conversations + agent launcher
            (patient scope only). */}
        {scope === 'patient' && (
          <section className="home-pane home-pane-list bg-white">
            <DesktopConversationsPane
              patientId={effectivePatientId}
              patientLabel={patientLabel}
              sessions={selectedPatientSessions}
              renderCard={(s) => renderCard(s, 'select')}
              patientAgents={patientAgents}
              onLaunchAgent={launchAgent}
              agentLabel={agentLabel}
              identityFor={identityFor}
              onBack={() => setSelectedPatientId(null)}
              isSessionsLoading={isSessionsLoading}
              isSessionsError={isSessionsError}
              isSessionsFetching={isSessionsFetching}
              onRetrySessions={() => void refetchSessions()}
              onHandover={
                selectedPatientAssignment ? () => setHandoverOpen(true) : undefined
              }
              receivedHandover={
                selectedPatientAssignment?.handover
                  ? {
                      handover: selectedPatientAssignment.handover,
                      receivedAt: selectedPatientAssignment.started_at,
                    }
                  : null
              }
              onOpenRapport={(sessionId) => setSelectedConversationId(sessionId)}
            />
          </section>
        )}

        {/* Pane 3 — the open conversation, the agent's NATIVE output. A face
            (agent open, no session yet) takes this slot until the first
            message mints one. `effectiveAgentFace` has TWO sources, in
            precedence order:
              1. `selectedAgentFace` — the nurse LAUNCHED an agent from a
                 tile. Outranks the derived one, but does not outrank the
                 registry: it is persisted, so after a rename/removal its slug
                 can be one nothing renders, and resolveEffectiveAgentFace
                 drops it to 2 (or to null) rather than show an empty pane.
              2. `fallbackAgentFace` — derived: the scope's newest session is
                 a quiz run, which the conversation default skips, so we show
                 that run's own agent instead of walking to a different one
                 (resolveFallbackAgentFace).
            A face wins over `selectedConversation` whenever set, and
            `effectiveConversationId` yields to it — so the two are never both
            active. An explicit conversation selection clears the launched face
            via the homeNav setters AND suppresses the derived one (it sets
            `selectionInScope`), so tapping a card always shows that card. */}
        <section className="home-pane home-pane-detail bg-white">
          {effectiveAgentFace ? (
            <AgentFacePane
              face={effectiveAgentFace}
              patientId={facePatientId}
              identityFor={identityFor}
              agentLabel={agentLabel}
              patientLabel={patientLabel}
              onSessionCreated={handleFaceSessionCreated}
              onBack={() => setSelectedAgentFace(null)}
            />
          ) : (
            <ConversationPreviewPane
              session={selectedConversation}
              patientLabel={patientLabel}
              identityFor={identityFor}
              agentLabel={agentLabel}
              onOpen={(s) => navigateToSession(s, (p) => navigate(p))}
              onBack={() => setSelectedConversationId(null)}
            />
          )}
        </section>
      </div>

      {/* Previous patients sheet (mobile + desktop pane-1 footer both open this) */}
      <PreviousPatientsSheet
        open={previousOpen}
        onOpenChange={setPreviousOpen}
        previousPatientIds={previousPatientIds}
        sessionsByPatient={sessionsByPatient}
        patientLabel={patientLabel}
        renderCard={(s) => renderCard(s, 'navigate')}
      />

      {/* Dialogs */}
      {handoverOpen && openShift && selectedPatientAssignment && effectivePatientId && (
        <HandoverDialog
          open={handoverOpen}
          onOpenChange={setHandoverOpen}
          patientId={effectivePatientId}
          patientLabel={patientLabel(effectivePatientId)}
          excludeNurseIds={[openShift.nurse_id]}
          onStarted={(session) => {
            // Open the new Rapport in the workspace, like launchAgent does.
            setPendingSession(session)
            setScope('patient')
            setSelectedConversationId(session.id)
          }}
        />
      )}
      {renameDialog && (
        <RenameSessionDialog
          open={!!renameDialog}
          onOpenChange={(open) => !open && setRenameDialog(null)}
          sessionId={renameDialog.id}
          currentTitle={renameDialog.title}
        />
      )}
      {archiveDialog && (
        <ArchiveSessionDialog
          open={!!archiveDialog}
          onOpenChange={(open) => !open && setArchiveDialog(null)}
          sessionId={archiveDialog.id}
          sessionTitle={archiveDialog.title}
        />
      )}
      {deleteDialog && (
        <DeleteSessionDialog
          open={!!deleteDialog}
          onOpenChange={(open) => !open && setDeleteDialog(null)}
          sessionId={deleteDialog.id}
          sessionTitle={deleteDialog.title}
        />
      )}
      {shareDialog && (
        <ShareDialog
          resourceId={shareDialog.id}
          resourceTitle={shareDialog.title}
          resourceType="session"
          open={!!shareDialog}
          onOpenChange={(open) => !open && setShareDialog(null)}
        />
      )}
    </div>
    </AgentLauncherContext.Provider>
  )
}

// ===========================================================================
// Header action — icon-only below lg, icon + label at lg+ (≥44px target).
// ===========================================================================
function HeaderAction({
  to,
  label,
  icon,
  children,
}: {
  to: string
  label: string
  icon: React.ReactNode
  children?: React.ReactNode
}) {
  return (
    <Link
      to={to}
      aria-label={label}
      className="home-action relative flex items-center gap-1.5 rounded-full text-neutral-600 active:scale-95 transition-transform"
    >
      {icon}
      <span className="home-action-label text-[13px] font-semibold">{label}</span>
      {children}
    </Link>
  )
}

// ===========================================================================
// Flat conversation list (Persönlich / Team) — newest-first, no agent
// grouping; each card carries its own agent badge. 4-state.
// ===========================================================================
function SessionList({
  sessions,
  emptyTitle,
  emptyDescription,
  renderCard,
  isLoading,
  isError,
  isFetching,
  onRetry,
}: {
  sessions: SessionResponse[]
  emptyTitle: string
  emptyDescription: string
  renderCard: (s: SessionResponse) => React.ReactNode
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  onRetry: () => void
}) {
  const { t } = useTranslation()

  if (isLoading && sessions.length === 0) return <GroupSkeleton />
  if (isError && sessions.length === 0) {
    return (
      <div className="py-6">
        <ErrorWithRetry error={t('home.loadError.title')} onRetry={isFetching ? undefined : onRetry} />
      </div>
    )
  }
  if (sessions.length === 0) return <EmptyState title={emptyTitle} description={emptyDescription} />

  return <div className="space-y-2 pt-1">{sessions.map(renderCard)}</div>
}

// ===========================================================================
// Pane 1 — the shift's patient list. Selecting a patient drives panes 2 + 3.
// Honest meta only (conversation count + needs-review count); no invented
// acuity/vitals (handoff §"do not invent vitals").
// ===========================================================================
function PatientRail({
  currentPatients,
  activePatientId,
  patientLabel,
  patientMeta,
  onSelect,
  onOpenPrevious,
  previousPatientCount,
  isLoading,
  isError,
  isFetching,
  onRetry,
}: {
  currentPatients: PatientGroup[]
  activePatientId: string | null
  patientLabel: (id: string) => string
  patientMeta: (id: string) => { total: number; needsReview: number }
  onSelect: (id: string) => void
  onOpenPrevious: () => void
  previousPatientCount: number
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  onRetry: () => void
}) {
  const { t } = useTranslation()

  if (isLoading && currentPatients.length === 0) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        <GroupSkeleton />
      </div>
    )
  }
  if (isError && currentPatients.length === 0) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        <ErrorWithRetry error={t('home.myPatients.loadError.title')} onRetry={isFetching ? undefined : onRetry} />
      </div>
    )
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-2">
      <div className="px-2 pt-1 pb-2 text-[12px] font-bold uppercase tracking-wide text-neutral-400">
        {t('home.shift')} · {currentPatients.length}
      </div>
      {currentPatients.length === 0 ? (
        <EmptyState title={t('home.myPatients.empty.title')} description={t('home.myPatients.empty.description')} />
      ) : (
        <div className="space-y-1">
          {currentPatients.map(({ patientId }) => {
            const active = patientId === activePatientId
            const meta = patientMeta(patientId)
            return (
              <button
                key={patientId}
                type="button"
                onClick={() => onSelect(patientId)}
                aria-current={active}
                className={cn(
                  'w-full min-h-[44px] px-2.5 py-2.5 rounded-xl flex items-center gap-3 text-left transition-colors',
                  active ? 'bg-primary-50 ring-1 ring-primary-100' : 'hover:bg-neutral-50 active:bg-neutral-100',
                )}
              >
                <span
                  className={cn(
                    'shrink-0 grid place-items-center w-9 h-9 rounded-[10px]',
                    active ? 'bg-primary-100 text-primary-700' : 'bg-neutral-50 text-neutral-400',
                  )}
                >
                  <User className="w-5 h-5" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className={cn('block font-display font-semibold truncate', active ? 'text-primary-700' : 'text-neutral-900')}>
                    {patientLabel(patientId)}
                  </span>
                  <span className="block text-[12px] text-neutral-400 truncate">
                    {meta.total} {t('home.section.conversations')}
                    {meta.needsReview > 0 && (
                      <span className="text-tone-warn">
                        {' · '}
                        {meta.needsReview} {t('conversation.state.needsReview')}
                      </span>
                    )}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      )}
      <PreviousPatientsRow count={previousPatientCount} onOpen={onOpenPrevious} />
    </div>
  )
}

// ===========================================================================
// Agent launcher — a search box + tile grid that starts a concrete agent.
// Shared by both scopes: per-patient (Patienten) and the nurse's own
// (Persönlich). No "new conversation" button — the Digital Twin (chat) tile IS
// the new conversation, shown as a speech bubble. Search scales with agents.
// ===========================================================================
function AgentLauncher({
  agents,
  onLaunch,
  label,
  agentLabel,
  identityFor,
}: {
  agents: AgentInfo[]
  onLaunch: (agent: AgentInfo) => void
  label: string
  agentLabel: (slug: string, fallback: string) => string
  identityFor: (slug: string) => AgentIdentity
}) {
  const { t } = useTranslation()
  const [agentQuery, setAgentQuery] = useState('')
  const filteredAgents = useMemo(() => {
    const q = agentQuery.trim().toLowerCase()
    if (!q) return agents
    return agents.filter((a) => agentLabel(a.slug, a.name).toLowerCase().includes(q))
  }, [agents, agentQuery, agentLabel])

  return (
    <div className="shrink-0 border-t border-neutral-200 bg-white p-3">
      <div className="px-1 pb-2 text-[11.5px] font-semibold text-neutral-400">{label}</div>
      {/* Agent search — filters the tiles; scales as more agents are added. */}
      <div className="relative mb-2.5">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400"
          aria-hidden="true"
        />
        <input
          type="text"
          value={agentQuery}
          onChange={(e) => setAgentQuery(e.target.value)}
          placeholder={t('home.searchAgents')}
          aria-label={t('home.searchAgents')}
          className="w-full rounded-full border border-neutral-200 bg-neutral-50 py-1.5 pl-8 pr-3 text-[12.5px] text-neutral-700 placeholder:text-neutral-400 focus:border-primary/40 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>
      {filteredAgents.length === 0 ? (
        <p className="px-1 py-2 text-[12px] text-neutral-400">{t('home.noAgentMatch')}</p>
      ) : (
        <div className="grid grid-cols-4 gap-2">
          {filteredAgents.map((agent) => {
            const identity = identityFor(agent.slug)
            const isChat = agent.slug === CHAT_AGENT_SLUG
            return (
              <button
                key={agent.slug}
                type="button"
                onClick={() => onLaunch(agent)}
                className="flex flex-col items-center gap-1.5 rounded-lg px-1 py-2 hover:bg-neutral-50 active:scale-95 transition-transform"
              >
                {isChat ? (
                  // The chat agent IS "new conversation" — a speech bubble, in
                  // its own accent, replacing the old "Neues Gespräch" button.
                  <span
                    className={cn(
                      'flex h-7 w-7 flex-none items-center justify-center rounded-md',
                      identity.accent.badge,
                    )}
                    aria-hidden="true"
                  >
                    <MessageSquare className="h-4 w-4" />
                  </span>
                ) : (
                  <AgentBadge identity={identity} size="lg" />
                )}
                <span className="text-[10.5px] font-semibold text-neutral-700 truncate max-w-full">
                  {agentLabel(agent.slug, agent.name)}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ===========================================================================
// DESKTOP pane 2 — selected patient's conversations + agent launcher.
// ===========================================================================
function DesktopConversationsPane({
  patientId,
  patientLabel,
  sessions,
  renderCard,
  patientAgents,
  onLaunchAgent,
  agentLabel,
  identityFor,
  onBack,
  isSessionsLoading,
  isSessionsError,
  isSessionsFetching,
  onRetrySessions,
  onHandover,
  receivedHandover,
  onOpenRapport,
}: {
  patientId: string | null
  patientLabel: (id: string) => string
  sessions: SessionResponse[]
  renderCard: (s: SessionResponse) => React.ReactNode
  patientAgents: AgentInfo[]
  onLaunchAgent: (agent: AgentInfo, patientId: string) => void
  agentLabel: (slug: string, fallback: string) => string
  identityFor: (slug: string) => AgentIdentity
  onBack: () => void
  isSessionsLoading: boolean
  isSessionsError: boolean
  isSessionsFetching: boolean
  onRetrySessions: () => void
  /** Present when this patient is in the nurse's own shift — opens the handover flow. */
  onHandover?: () => void
  /** The handover this patient ARRIVED with (incoming), while this custody lasts. */
  receivedHandover: { handover: AssignmentHandover; receivedAt: string } | null
  onOpenRapport: (sessionId: string) => void
}) {
  const { t } = useTranslation()

  if (!patientId) {
    return <EmptyState title={t('home.myPatients.empty.title')} description={t('home.myPatients.empty.description')} />
  }

  // Sessions arrive newest-first from the API — render in that order.
  return (
    <>
      {/* Patient context header (label only — no invented vitals/status).
          The back chevron returns to the patient list in the narrow drill. */}
      <div className="shrink-0 flex items-center gap-2 px-4 py-3 border-b border-neutral-200 bg-white">
        <button
          type="button"
          onClick={onBack}
          aria-label={t('home.shift')}
          className="home-pane-back -ml-1 shrink-0 items-center text-neutral-500 active:scale-95 transition-transform"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <h2 className="min-w-0 flex-1 text-[20px] font-display font-bold text-neutral-900 truncate">{patientLabel(patientId)}</h2>
        {onHandover && (
          <button
            type="button"
            onClick={onHandover}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-primary-100 bg-primary-50 px-3 py-1.5 text-[12.5px] font-semibold text-primary-700 active:scale-95 transition-transform"
          >
            <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden="true" />
            {t('shifts.handover.action', { defaultValue: 'Hand over' })}
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {receivedHandover && (
          <div className="pb-3">
            <HandoverCard
              handover={receivedHandover.handover}
              receivedAt={receivedHandover.receivedAt}
              onOpenRapport={onOpenRapport}
            />
          </div>
        )}
        <div className="px-1 pb-2 text-[12px] font-bold uppercase tracking-wide text-neutral-400">
          {t('home.section.conversations')}
        </div>
        {isSessionsLoading && sessions.length === 0 ? (
          <GroupSkeleton />
        ) : isSessionsError && sessions.length === 0 ? (
          <ErrorWithRetry error={t('home.loadError.title')} onRetry={isSessionsFetching ? undefined : onRetrySessions} />
        ) : sessions.length === 0 ? (
          <p className="px-1 py-3 text-[13px] text-neutral-400">{t('home.myPatients.noSessions')}</p>
        ) : (
          <div className="space-y-2">{sessions.map(renderCard)}</div>
        )}
      </div>

      <AgentLauncher
        agents={patientAgents}
        onLaunch={(agent) => onLaunchAgent(agent, patientId)}
        label={t('home.launcherFor', { label: patientLabel(patientId) })}
        agentLabel={agentLabel}
        identityFor={identityFor}
      />
    </>
  )
}

// ===========================================================================
// DESKTOP — the open conversation. Renders the agent's NATIVE output (the last
// assistant message's blocks/text via the read-only chat renderers), not a
// flattened summary. "Agent öffnen" is a quiet secondary — the representation
// is the point; opening the sub-app is optional.
// ===========================================================================
function ConversationPreviewPane({
  session,
  patientLabel,
  identityFor,
  agentLabel,
  onOpen,
  onBack,
}: {
  session: SessionResponse | null
  patientLabel: (id: string) => string
  identityFor: (slug: string) => AgentIdentity
  agentLabel: (slug: string, fallback: string) => string
  onOpen: (s: SessionResponse) => void
  onBack: () => void
}) {
  const { t } = useTranslation()

  if (!session) {
    return (
      <div className="flex h-full flex-col">
        <button
          type="button"
          onClick={onBack}
          aria-label={t('home.section.conversations')}
          className="home-pane-back shrink-0 items-center gap-1 px-4 py-3 text-[13px] font-semibold text-primary-700 active:scale-95 transition-transform"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          {t('home.section.conversations')}
        </button>
        <div className="flex-1 grid place-items-center p-8">
          <EmptyState title={t('home.selectConversation.title')} description={t('home.selectConversation.description')} />
        </div>
      </div>
    )
  }

  const identity = identityFor(sessionSurfaceSlug(session))
  const name = agentLabel(identity.slug, identity.name)
  // If this agent has a registered interactive app, the detail pane renders it
  // (the agent working in the frame) instead of the read-only summary.
  const detailView = getAgentDetailView(sessionSurfaceSlug(session))
  const context = session.subject?.type === 'patient'
    ? patientLabel(session.subject.id)
    : t(identity.scope === 'team' ? 'home.scope.team' : 'home.scope.personal')

  return (
    <div className="flex h-full flex-col">
      {/* Agent strip — "Agent öffnen" demoted to a quiet secondary link */}
      <div className="shrink-0 flex items-center gap-3 px-5 py-3 border-b border-neutral-200">
        <button
          type="button"
          onClick={onBack}
          aria-label={t('home.section.conversations')}
          className="home-pane-back -ml-1 shrink-0 items-center text-neutral-500 active:scale-95 transition-transform"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <AgentBadge identity={identity} size="xl" />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-[15px] font-display font-bold text-neutral-900 truncate">{name}</span>
            <span className="text-[11.5px] text-neutral-500 truncate">· {context}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onOpen(session)}
          className="shrink-0 inline-flex items-center gap-0.5 text-[12.5px] font-semibold text-primary-700 active:scale-95 transition-transform"
        >
          {t('home.openAgent')}
          <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      {detailView ? (
        // The agent's INTERACTIVE app, working in the frame: full-bleed below
        // the strip, owning its own scroll. (compatibility matrix today; care-planner,
        // roster, knowledge-profile next — one registry entry each.)
        <div className="flex-1 min-h-0 overflow-hidden">
          <AgentDetailPane key={session.id} session={session} View={detailView} />
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
          {session.title && (
            <h2 className="text-[19px] font-display font-bold tracking-tight text-neutral-900">{session.title}</h2>
          )}
          <ConversationOutput session={session} onOpen={onOpen} />
        </div>
      )}
    </div>
  )
}

// ===========================================================================
// DESKTOP pane 3, session-less — an agent open with NO session yet (launchAgent
// deferred creation; see the homeNav store). Strip mirrors ConversationPreviewPane's
// (badge, name, scope context). Its "Agent öffnen" needs no session: an agent
// with its own sub-app opens there full-screen on the SAME screen the frame
// shows (the inner path of /home/a/<slug>/…, query and hash included), so a
// Studio open at /home/a/protocols/studio/<id>/v1 opens at /studio/<id>/v1.
// An agent the main app is the only surface of has no full screen to offer.
// ===========================================================================
function AgentFacePane({
  face,
  patientId,
  identityFor,
  agentLabel,
  patientLabel,
  onSessionCreated,
  onBack,
}: {
  face: AgentFaceSelection
  /** The frame Home is showing, from the same derivation panes 1 and 2 use. */
  patientId: string | null
  identityFor: (slug: string) => AgentIdentity
  agentLabel: (slug: string, fallback: string) => string
  patientLabel: (id: string) => string
  onSessionCreated: (session: SessionResponse) => void
  onBack: () => void
}) {
  const { t } = useTranslation()
  const identity = identityFor(face.slug)
  const name = agentLabel(identity.slug, identity.name)
  const context = patientId
    ? patientLabel(patientId)
    : t(identity.scope === 'team' ? 'home.scope.team' : 'home.scope.personal')
  // A face that only works bound to a session (the shared chat view) has no
  // session here, so it opens in the session-less chat surface instead; every
  // other face mounts session-less by design. Asked of the registry, never
  // hand-listed — see faceRequiresSession.
  const isChatFaced = faceRequiresSession(face.slug)
  const { pathname, search, hash } = useLocation()
  const facePath = agentPathFromFramePath(face.slug, pathname)
  const fullScreenUrl = agentFullScreenUrl(face.slug, `${facePath}${search}${hash}`)

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 flex items-center gap-3 px-5 py-3 border-b border-neutral-200">
        <button
          type="button"
          onClick={onBack}
          aria-label={t('home.section.conversations')}
          className="home-pane-back -ml-1 shrink-0 items-center text-neutral-500 active:scale-95 transition-transform"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <AgentBadge identity={identity} size="xl" />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-[15px] font-display font-bold text-neutral-900 truncate">{name}</span>
            <span className="text-[11.5px] text-neutral-500 truncate">· {context}</span>
          </div>
        </div>
        {fullScreenUrl && (
          <a
            href={fullScreenUrl}
            className="shrink-0 inline-flex items-center gap-0.5 text-[12.5px] font-semibold text-primary-700 active:scale-95 transition-transform"
          >
            {t('home.openAgent')}
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        )}
      </div>

      {isChatFaced ? (
        <ChatFace agentSlug={face.slug} patientId={patientId} onSessionCreated={onSessionCreated} />
      ) : (
        <div className="flex-1 min-h-0 overflow-hidden">
          <BoardFace slug={face.slug} patientId={patientId} onSessionCreated={onSessionCreated} />
        </div>
      )}
    </div>
  )
}

// The registry's own board view, mounted with NO session — the board content
// is full (AccessBoardConnected precedent); the composer/side-channel is now
// ALSO live session-less, wired through useDeferredFirstSend by each wrapper
// itself (facePatientId + onSessionCreated) — see CarePlanBoardConnected /
// AllocationBoardConnected / AccessBoardConnected / TeamAccessBoardConnected.
// getAgentDetailView's declared return type requires `session` (the chat view,
// the one session-BOUND registry entry, genuinely needs one); every other view
// accepts `session?:` plus the two face-mode props, so this cast reflects their
// real, wider signature rather than lying about it. AgentFacePane keeps the
// session-bound entries away from here by asking faceRequiresSession.
function BoardFace({
  slug,
  patientId,
  onSessionCreated,
}: {
  slug: string
  patientId: string | null
  onSessionCreated: (session: SessionResponse) => void
}) {
  // Derived from the registry's own props type — only `session` is widened to
  // optional (every session-less view genuinely accepts session?; the chat
  // view requires one and never reaches here — see faceRequiresSession).
  // Deriving keeps any future registry-prop change flowing
  // through instead of being shadowed by a hand-written parallel shape.
  const View = getAgentDetailView(slug) as ComponentType<
    Omit<AgentDetailViewProps, 'session'> & { session?: AgentDetailViewProps['session'] }
  > | null
  // Unreachable: resolveEffectiveAgentFace drops a face whose slug the registry
  // no longer has an entry for, so pane 3 falls back to the scope default
  // rather than reaching here. (It used to hold because launchAgent checked the
  // same registry and the face lived only in memory; the face is persisted now,
  // so a slug from a previous deploy is what that check is for.)
  if (!View) return null
  return (
    // No session yet, so no entry path to derive: a launched face always opens
    // at the agent's root screen.
    <AgentFrame agentSlug={slug}>
      <View facePatientId={patientId} onSessionCreated={onSessionCreated} />
    </AgentFrame>
  )
}

// Session-less chat face: the SAME empty-state MessageList renders for a
// brand-new session (t('session.empty')), plus the composer. useDeferredFirstSend
// owns the whole deferred-create-then-flush lifecycle (Session.tsx's
// `/sessions/new` route and every board wrapper's side-channel share the same
// hook) — this component never creates a session itself, it only calls send().
function ChatFace({
  agentSlug,
  patientId,
  onSessionCreated,
}: {
  agentSlug: string
  patientId: string | null
  onSessionCreated: (session: SessionResponse) => void
}) {
  const { t } = useTranslation()
  const deferred = useDeferredFirstSend({
    sessionId: null,
    agentSlug,
    patientId: patientId ?? undefined,
    onCreated: onSessionCreated,
  })
  return (
    <div className="flex h-full flex-col bg-neutral-50">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-4 py-4">
          <MessageList messages={[]} isLoading={false} isPending={false} emptyMessage={t('session.empty')} />
        </div>
      </div>
      <div className="shrink-0 border-t border-neutral-200/50 bg-neutral-50 pb-safe">
        <div className="mx-auto max-w-4xl px-4 py-3">
          <ChatInput
            onSend={(content) => void deferred.send(content)}
            isPending={deferred.isPending}
            placeholder={t('chat.placeholder')}
          />
        </div>
      </div>
    </div>
  )
}

// Amber source chip — only rendered with a meaningful label (guards the
// backend's `()` placeholder bug).
function SourceChip({ label, onView }: { label: string; onView: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="rounded-2xl border border-citation-100 bg-citation-50 overflow-hidden">
      <div className="flex">
        <div className="w-1 shrink-0 bg-citation-500" aria-hidden="true" />
        <div className="flex items-start gap-2.5 p-3.5">
          <FileText className="mt-0.5 h-4 w-4 shrink-0 text-citation-700" aria-hidden="true" />
          <div className="min-w-0">
            <div className="text-[13.5px] font-bold text-citation-900">{label}</div>
            <button
              type="button"
              onClick={onView}
              className="mt-0.5 inline-flex items-center gap-0.5 text-[12.5px] font-semibold text-citation-700 active:scale-95 transition-transform"
            >
              {t('home.viewSource')}
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Fetches the full session (with messages) and renders the agent's INTERACTIVE
// app — the agent working IN the frame — from the agentDetailViews registry.
// Loading shows a skeleton; a hard fetch failure shows retry, never a blank.
function AgentDetailPane({
  session,
  View,
}: {
  session: SessionResponse
  View: ComponentType<AgentDetailViewProps>
}) {
  const { t } = useTranslation()
  const { data, isLoading, refetch } = useSession(session.id)
  if (data)
    return (
      <AgentFrame agentSlug={sessionSurfaceSlug(session)}>
        <View session={data} />
      </AgentFrame>
    )
  if (isLoading) {
    return (
      <div className="h-full space-y-3 p-5">
        <Skeleton className="h-6 w-1/2 rounded" />
        <Skeleton className="h-[70%] w-full rounded-2xl" />
      </div>
    )
  }
  return (
    <div className="grid h-full place-items-center p-8">
      <ErrorWithRetry error={t('home.loadError.title')} onRetry={() => void refetch()} />
    </div>
  )
}

// Fetches the selected conversation's detail and renders the latest agent
// message's text (read-only), 4-state; falls back to the session's stored
// summary when there is no renderable output or on error. The widget cards
// this preview used to inline died with the conversation domain's move — a
// message's payload is text/tool/citation blocks now.
function ConversationOutput({
  session,
  onOpen,
}: {
  session: SessionResponse
  onOpen: (s: SessionResponse) => void
}) {
  const { t } = useTranslation()
  const { data, isLoading, isError, isFetching, refetch } = useSession(session.id)

  const lastAssistant = useMemo<MessageResponse | null>(() => {
    const msgs = data?.messages.items ?? []
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'agent' && msgs[i].type === CONVERSATION_MESSAGE_TYPE) return msgs[i]
    }
    return null
  }, [data])

  const lastText = conversationText(lastAssistant?.payload)
  const lastCitations = conversationCitations(lastAssistant?.payload)
  const showText = lastText.trim().length > 0

  // Roster has no per-block inline render, and it is no longer folded out of the
  // transcript: the roster is `GET /shifts`, so the preview reads the team's own
  // rows. `enabled` keeps this to roster sessions — every other agent's pane
  // makes no roster call at all.
  const isRoster = sessionSurfaceSlug(session) === ROSTER_AGENT_SLUG
  const rosterTeams = useRosterTeams({}, { enabled: isRoster })
  const rosterTeamId = rosterTeams.data?.[0]?.id
  const rosterMembers = useRosterTeamMembers(rosterTeamId, { enabled: isRoster })
  const rosterShifts = useRosterShifts({ teamId: rosterTeamId }, { enabled: isRoster })
  const rosterNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const m of rosterMembers.data ?? []) {
      if (m.user_ref?.display) map.set(m.user, m.user_ref.display)
    }
    return map
  }, [rosterMembers.data])
  const rosterEntries = useMemo(
    () => (isRoster ? shiftsToEntries(rosterShifts.data, rosterNames) : []),
    [isRoster, rosterShifts.data, rosterNames],
  )
  const hasRoster = rosterEntries.length > 0
  const hasNative = showText || hasRoster

  // A plain-text answer carries no embedded citation chip of its own — surface
  // the top source from the message's citation blocks.
  const rawLabel = lastCitations[0]?.document
  const sourceLabel = isMeaningfulSourceLabel(rawLabel) ? rawLabel.trim() : null

  // LOADING (no cache): skeleton.
  if (isLoading && !data) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    )
  }

  // DATA: the agent's native representation.
  if (hasNative && lastAssistant) {
    return (
      <div className="space-y-4">
        {showText && (
          <ConversationContext.Provider value={session.id}>
            <MarkdownContent content={lastText} className="text-[14.5px] leading-relaxed text-neutral-800" />
          </ConversationContext.Provider>
        )}
        {hasRoster && <RosterScheduleCard entries={rosterEntries} />}
        {/* A plain-text answer carries no embedded citation — surface the top source. */}
        {!hasRoster && sourceLabel && (
          <SourceChip label={sourceLabel} onView={() => onOpen(session)} />
        )}
      </div>
    )
  }

  // FALLBACK: no inline-renderable output yet (or a fetch error) — the
  // session's stored summary + source, never blank or a broken chip.
  return (
    <div className="space-y-4">
      {session.summary ? (
        <div className="rounded-2xl border border-neutral-200 bg-neutral-50 overflow-hidden">
          <div className="flex">
            <div className="w-1.5 shrink-0 bg-tone-ok" aria-hidden="true" />
            <p className="p-4 text-[14.5px] leading-relaxed text-neutral-800">{session.summary}</p>
          </div>
        </div>
      ) : (
        <p className="text-[14px] text-neutral-400">{t('home.noAnswerYet')}</p>
      )}
      {sourceLabel && <SourceChip label={sourceLabel} onView={() => onOpen(session)} />}
      {isError && (
        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isFetching}
          className="text-[13px] text-primary active:scale-95 transition disabled:opacity-50"
        >
          {t('home.loadError.retry')}
        </button>
      )}
    </div>
  )
}

// ===========================================================================
// Previous patients row + sheet (preserved).
// ===========================================================================
function PreviousPatientsRow({ count, onOpen }: { count: number; onOpen: () => void }) {
  const { t } = useTranslation()
  if (count === 0) return null
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'w-full mt-2 min-h-[44px] px-4 py-3 rounded-lg flex items-center justify-between gap-2',
        'text-[14px] text-neutral-500 active:bg-neutral-100 transition-colors',
      )}
    >
      <span>{t('home.previousPatients.row', { count })}</span>
      <ChevronRight className="w-4 h-4" aria-hidden="true" />
    </button>
  )
}

function PreviousPatientsSheet({
  open,
  onOpenChange,
  previousPatientIds,
  sessionsByPatient,
  patientLabel,
  renderCard,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  previousPatientIds: string[]
  sessionsByPatient: Map<string, SessionResponse[]>
  patientLabel: (id: string) => string
  renderCard: (s: SessionResponse) => React.ReactNode
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState<string | null>(null)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-2xl pb-safe">
        <SheetHeader>
          <SheetTitle>{t('home.previousPatients.title')}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {previousPatientIds.length === 0 ? (
            <EmptyState
              title={t('home.previousPatients.empty.title')}
              description={t('home.previousPatients.empty.description')}
            />
          ) : (
            previousPatientIds.map((patientId) => {
              const isOpen = expanded === patientId
              const patientSessions = sessionsByPatient.get(patientId) ?? EMPTY_SESSIONS
              const panelId = `previous-patient-sessions-${patientId}`
              return (
                <div key={patientId} className="rounded-lg border border-neutral-200/70 bg-white overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : patientId)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    className="w-full flex items-center justify-between gap-2 px-4 py-3 min-h-[44px] active:bg-neutral-50"
                  >
                    <span className="flex items-baseline gap-2 min-w-0">
                      <span className="font-display font-semibold text-neutral-900 truncate">{patientLabel(patientId)}</span>
                      <span className="text-[13px] text-neutral-400 shrink-0">{patientSessions.length}</span>
                    </span>
                    <ChevronDown
                      className={cn('w-4 h-4 text-neutral-400 shrink-0 transition-transform duration-150', isOpen && 'rotate-180')}
                      aria-hidden="true"
                    />
                  </button>
                  {isOpen && (
                    <div id={panelId} className="px-3 pb-3">
                      {patientSessions.length === 0 ? (
                        <p className="px-1 py-2 text-[13px] text-neutral-400">{t('home.previousPatients.noSessions')}</p>
                      ) : (
                        <div className="space-y-2">{patientSessions.map(renderCard)}</div>
                      )}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

// ===========================================================================
// Shared skeleton for a list of grouped sessions.
// ===========================================================================
function GroupSkeleton() {
  return (
    <div className="space-y-3 pt-1">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="p-4 rounded-xl bg-white border border-neutral-200/50">
          <Skeleton className="h-5 w-1/3 mb-3" />
          <Skeleton className="h-4 w-3/4 mb-2" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ))}
    </div>
  )
}
